// Pure helpers for pointing a defense-plan feature at a specific house
// (Glenn's Sept 16 2026 ask: "I want to pick the houses... associate those
// with this solution"), rather than at a drawn shape. No Leaflet, no
// Firebase, no store — useDefensePlanDraw.web.ts runs these against the
// plain Structure[] already loaded in structuresStore, without pulling any
// of that store's own machinery in here. Mirrors planGeometry.ts's own
// "pure geometry, store/hook does the wiring" split.
import type { DefensePlanFeature, DefenseStrategy, Structure } from '../types';
import { haversineMeters } from './planGeometry';

// How close a map click has to land to a structure's centroid to count as
// "clicked that house" in home-draw mode. Structures at the zoom home mode
// needs (property pins visible) commonly sit tens of feet apart; wider than
// this risks picking the wrong neighbor.
export const NEAREST_STRUCTURE_MAX_METERS = 30;

/**
 * The closest of `structures` to `point`, or null when none are within
 * `maxMeters`. A tie (equal distance) keeps whichever came first in the
 * array — good enough for a UI pick, and deterministic for tests.
 */
export function nearestStructure(
  point: { lat: number; lng: number },
  structures: Structure[],
  maxMeters: number
): Structure | null {
  let best: Structure | null = null;
  let bestDist = Infinity;
  for (const structure of structures) {
    const dist = haversineMeters(point, {
      lat: structure.centroid.latitude,
      lng: structure.centroid.longitude,
    });
    if (dist < bestDist) {
      bestDist = dist;
      best = structure;
    }
  }
  return best && bestDist <= maxMeters ? best : null;
}

/**
 * True when `features` already has a feature for this exact
 * strategy+structureId pair. Glenn's ask allows several different
 * strategies on one home (active defense, paint, hardening, each its own
 * feature) but not the same strategy added to the same house twice — this
 * is the guard useDefensePlanDraw.web.ts's home-mode click runs before
 * calling addFeature.
 */
export function hasHomeFeature(
  features: Pick<DefensePlanFeature, 'strategy' | 'structureId'>[],
  strategy: DefenseStrategy,
  structureId: string
): boolean {
  return features.some((f) => f.strategy === strategy && f.structureId === structureId);
}

/**
 * Single-line "123 Main St, Big Bear Lake, CA 92315" address for a
 * structure, joining whichever of address/city/state/zip it actually has.
 * Falls back to the coordinates (to 5 decimal places, ~1m precision) when a
 * structure carries no address at all — some GeoJSON shards omit it — so a
 * home feature's row/tooltip never shows nothing.
 */
export function formatStructureAddress(structure: Structure): string {
  const cityStateZip =
    [structure.city, structure.state].filter(Boolean).join(', ') +
    (structure.zip ? ` ${structure.zip}` : '');
  const parts = [structure.address, cityStateZip].filter((p) => !!p && p.trim().length > 0);
  if (parts.length > 0) return parts.join(', ');
  return `${structure.centroid.latitude.toFixed(5)}, ${structure.centroid.longitude.toFixed(5)}`;
}

// How far from a new plan's first defense a home can be and still name the
// plan. A fire break is often drawn along a road or a ridge rather than on a
// lot, so this is wider than a click's reach.
export const PLAN_PLACE_MAX_METERS = 250;

/**
 * The street address of the home nearest `point`, for naming a new plan
 * ("Plan near 977 Club View Drive"). Only the street line: city and state
 * are the same for every plan in an area and would crowd the title. Null
 * when no loaded home with an address is within PLAN_PLACE_MAX_METERS.
 */
export function nearestStreetAddress(
  point: { lat: number; lng: number },
  structures: Structure[]
): string | null {
  const withAddress = structures.filter((s) => !!s.address?.trim());
  const match = nearestStructure(point, withAddress, PLAN_PLACE_MAX_METERS);
  return match?.address?.trim() ?? null;
}
